import type { Virtualizer } from '@tanstack/react-virtual';

import { useSwimlaneRootProps } from '../../context';
import { CELL_GAP } from '../constants';
import { getGroupHeaderOffsets } from '../layoutMetrics';
import { SwimlaneGridCell } from './SwimlaneGridCell';

interface ISwimlaneGrid {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export function SwimlaneGrid({ columnVirtualizer, rowVirtualizer }: ISwimlaneGrid) {
  const { rowHeaders, columnHeaders, showGridLines } = useSwimlaneRootProps();
  const rowVirtualItems = rowVirtualizer.getVirtualItems();
  const columnVirtualItems = columnVirtualizer.getVirtualItems();

  const isGroupHeaderAt = (index: number) => {
    const header = rowHeaders[index];
    return !!header && 'isGroupHeader' in header && header.isGroupHeader === true;
  };
  const groupHeaderOffsets = getGroupHeaderOffsets(rowHeaders.length, (index) =>
    isGroupHeaderAt(index) ? rowVirtualizer.options.estimateSize(index) : undefined,
  );
  const totalGroupHeaderOffset = groupHeaderOffsets[rowHeaders.length] ?? 0;

  const gridHeight = rowVirtualizer.getTotalSize() - totalGroupHeaderOffset;

  return (
    <div
      style={{
        height: `${gridHeight}px`,
        width: `${columnVirtualizer.getTotalSize()}px`,
        gridArea: 'grid',
        position: 'relative',
        // Create a stacking context so row divs' z-indexes (which scale with the rendered
        // row count) stay below the sticky column headers.
        isolation: 'isolate',
      }}
    >
      {/* Full-height vertical column dividers. Rendered at the grid level so they span
          uninterrupted across rows that don't render cells (group headers, empty rows
          from hidden headers). Per-cell :before pseudo-elements on SwimlaneGridCell would
          otherwise leave visible gaps in those rows (NEX-11268). */}
      {showGridLines &&
        columnVirtualItems.map((virtualColumn) => {
          if (virtualColumn.index >= columnHeaders.length - 1) return null;
          return (
            <div
              key={`col-divider-${virtualColumn.key}`}
              aria-hidden="true"
              className="absolute top-0 bottom-0 w-px bg-border-neutral-bold/12 pointer-events-none"
              style={{
                left: `${virtualColumn.start + virtualColumn.size + CELL_GAP.value / 2}px`,
                zIndex: 0,
              }}
            />
          );
        })}
      {rowVirtualItems.map((virtualRow, index) => {
        const isGroupHeader = isGroupHeaderAt(virtualRow.index);
        const adjustedVirtualRow =
          virtualRow.index < rowHeaders.length && !isGroupHeader
            ? {
                ...virtualRow,
                start: virtualRow.start - (groupHeaderOffsets[virtualRow.index] ?? 0),
              }
            : virtualRow;
        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
            // assign progressively lower z-index to ensure that the higher rows are rendered on top
            // this is important for visibility of InlineAdd button
            style={{ position: 'relative', zIndex: rowVirtualItems.length - index }}
          >
            {!isGroupHeader &&
              columnVirtualItems.map((virtualColumn) => (
                <SwimlaneGridCell
                  key={`${virtualRow.key}-${virtualColumn.key}`}
                  virtualColumn={virtualColumn}
                  virtualRow={adjustedVirtualRow}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}
